import React, { useEffect, useMemo, useState } from "react";
import api, { API } from "@/lib/api";
import { PageHeader, PageContent } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Facebook, Instagram, Twitter, Plug, Unplug, Send, CheckCircle2, AlertTriangle, Eye, Save } from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORMS = {
  facebook: { page_id: "", page_token: "", page_name: "" },
  instagram: { ig_user_id: "", username: "" },
  x: { api_key: "", api_secret: "", access_token: "", access_secret: "", username: "" },
};

const DEFAULT_TEMPLATES = {
  telegram: { enabled: true, text: "<b>{title}</b>\n\n{post}\n\n{hashtags}", hashtags: "#CalcioNews24 #CalcioHub", cta: "" },
  facebook: { enabled: true, text: "{title}\n\n{post}\n\n{cta}\n\n{hashtags}", hashtags: "#CalcioNews24 #CalcioHub", cta: "Segui CalcioNews24 per tutte le ultime notizie." },
  instagram: { enabled: true, text: "{title}\n\n{post}\n\n{cta}\n\n{hashtags}", hashtags: "#calcionews24 #calciohub #calcio #seriea", cta: "Commenta la tua opinione e seguici per altri aggiornamenti." },
  x: { enabled: true, text: "{title}\n\n{post}\n\n{hashtags}", hashtags: "#CalcioNews24 #Calcio", cta: "" },
};

const PLATFORMS = [
  { id: "telegram", label: "Telegram", color: "text-sky-300", preview: "verticale" },
  { id: "facebook", label: "Facebook", icon: Facebook, color: "text-blue-400", preview: "orizzontale" },
  { id: "instagram", label: "Instagram Business", icon: Instagram, color: "text-pink-400", preview: "1080x1080" },
  { id: "x", label: "X", icon: Twitter, color: "text-slate-200", preview: "landscape" },
];

function iconFor(platform) {
  const found = PLATFORMS.find(p => p.id === platform);
  return found?.icon || Send;
}

function AccountCard({ platform, account, form, setForm, onSave, onDisconnect, onTest, onPublishTest }) {
  const Icon = iconFor(platform.id);
  const safeAccount = account || {};
  const safeForm = form || EMPTY_FORMS[platform.id] || {};
  const connected = !!safeAccount.connected;

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-md p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-slate-950 border border-slate-800 flex items-center justify-center">
            <Icon className={platform.color} size={20} />
          </div>
          <div>
            <div className="font-bold text-white">{platform.label}</div>
            <div className={`text-xs mt-0.5 ${connected ? "text-emerald-300" : "text-slate-500"}`}>
              {connected ? "Collegato" : "Non collegato"}
            </div>
          </div>
        </div>
        {connected ? <CheckCircle2 size={18} className="text-emerald-400" /> : <AlertTriangle size={18} className="text-slate-600" />}
      </div>

      <div className="grid grid-cols-2 gap-2 mt-4 text-xs">
        <div className="rounded-md bg-slate-950 border border-slate-800 p-2">
          <div className="text-slate-500">Ultima pubblicazione</div>
          <div className="text-slate-300 mt-1">{safeAccount.last_publish_at ? new Date(safeAccount.last_publish_at).toLocaleString("it-IT") : "Mai"}</div>
        </div>
        <div className="rounded-md bg-slate-950 border border-slate-800 p-2">
          <div className="text-slate-500">Ultimo errore</div>
          <div className="text-red-300 mt-1 line-clamp-2">{safeAccount.last_error || "Nessuno"}</div>
        </div>
      </div>

      <div className="mt-4 space-y-3">
        {platform.id === "facebook" && (
          <>
            <div><Label>Page ID</Label><Input value={safeForm.page_id || ""} onChange={e=>setForm("facebook", { page_id: e.target.value })} className="bg-slate-950 border-slate-800 mt-1 font-mono text-xs" placeholder={safeAccount.page_id || "123456789"} /></div>
            <div><Label>Nome pagina</Label><Input value={safeForm.page_name || ""} onChange={e=>setForm("facebook", { page_name: e.target.value })} className="bg-slate-950 border-slate-800 mt-1" placeholder={safeAccount.page_name || "CalcioNews24"} /></div>
            <div><Label>Page Access Token</Label><Input type="password" value={safeForm.page_token || ""} onChange={e=>setForm("facebook", { page_token: e.target.value })} className="bg-slate-950 border-slate-800 mt-1 font-mono text-xs" placeholder={safeAccount.token_preview || "EAAB..."} /></div>
          </>
        )}

        {platform.id === "instagram" && (
          <>
            <div><Label>Instagram Business Account ID</Label><Input value={safeForm.ig_user_id || ""} onChange={e=>setForm("instagram", { ig_user_id: e.target.value })} className="bg-slate-950 border-slate-800 mt-1 font-mono text-xs" placeholder={safeAccount.ig_user_id || "178414..."} /></div>
            <div><Label>Username</Label><Input value={safeForm.username || ""} onChange={e=>setForm("instagram", { username: e.target.value })} className="bg-slate-950 border-slate-800 mt-1" placeholder={safeAccount.username || "calcionews24"} /></div>
            <p className="text-xs text-slate-500">Instagram usa il Page Access Token Facebook collegato alla pagina Meta.</p>
          </>
        )}

        {platform.id === "x" && (
          <>
            <div><Label>Username</Label><Input value={safeForm.username || ""} onChange={e=>setForm("x", { username: e.target.value })} className="bg-slate-950 border-slate-800 mt-1" placeholder={safeAccount.username || "CalcioNews24"} /></div>
            <div><Label>API Key</Label><Input type="password" value={safeForm.api_key || ""} onChange={e=>setForm("x", { api_key: e.target.value })} className="bg-slate-950 border-slate-800 mt-1 font-mono text-xs" placeholder={safeAccount.api_key_preview || ""} /></div>
            <div><Label>API Secret</Label><Input type="password" value={safeForm.api_secret || ""} onChange={e=>setForm("x", { api_secret: e.target.value })} className="bg-slate-950 border-slate-800 mt-1 font-mono text-xs" /></div>
            <div><Label>Access Token</Label><Input type="password" value={safeForm.access_token || ""} onChange={e=>setForm("x", { access_token: e.target.value })} className="bg-slate-950 border-slate-800 mt-1 font-mono text-xs" placeholder={safeAccount.access_token_preview || ""} /></div>
            <div><Label>Access Secret</Label><Input type="password" value={safeForm.access_secret || ""} onChange={e=>setForm("x", { access_secret: e.target.value })} className="bg-slate-950 border-slate-800 mt-1 font-mono text-xs" /></div>
          </>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        <Button onClick={()=>onSave(platform.id)} size="sm" className="bg-blue-600 hover:bg-blue-700"><Plug size={14} className="mr-1" />Collega</Button>
        <Button onClick={()=>onTest(platform.id)} size="sm" variant="outline" className="border-slate-700">Test</Button>
        <Button onClick={()=>onPublishTest(platform.id)} size="sm" variant="outline" className="border-slate-700">Pubblica test</Button>
        <Button onClick={()=>onDisconnect(platform.id)} size="sm" variant="ghost" className="text-red-400"><Unplug size={14} className="mr-1" />Disconnetti</Button>
      </div>
    </div>
  );
}

function PreviewCard({ platform, item }) {
  const meta = PLATFORMS.find(p => p.id === platform) || {};
  const Icon = iconFor(platform);
  const imageUrl = item?.image_url ? `${API.replace(/\/api$/, "")}${item.image_url}` : "";

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-md overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <Icon size={16} className={meta.color} />
          <span className="font-bold text-white">{meta.label}</span>
        </div>
        <span className="text-[10px] uppercase font-mono text-slate-500">{meta.preview}</span>
      </div>
      {imageUrl && (
        <div className="bg-slate-950 flex justify-center">
          <img src={imageUrl} alt="" className="max-h-72 w-full object-contain" />
        </div>
      )}
      <div className="p-4 text-sm text-slate-200 whitespace-pre-wrap leading-relaxed min-h-32">
        {item?.text || "Anteprima non disponibile"}
      </div>
    </div>
  );
}

export default function SocialMedia() {
  const [text, setText] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [selected, setSelected] = useState(["facebook"]);
  const [history, setHistory] = useState([]);
  const [accounts, setAccounts] = useState({});
  const [forms, setForms] = useState(EMPTY_FORMS);
  const [templates, setTemplates] = useState(DEFAULT_TEMPLATES);
  const [socialSettings, setSocialSettings] = useState({ social_public_base_url: "" });
  const [activeTemplate, setActiveTemplate] = useState("facebook");
  const [preview, setPreview] = useState({});
  const [loadError, setLoadError] = useState("");

  const socialPlatforms = useMemo(() => PLATFORMS.filter(p => p.id !== "telegram"), []);

  const normalizeAccounts = (value) => {
    const source = value && typeof value === "object" ? value : {};
    return {
      facebook: { connected: false, page_id: "", page_name: "", token_preview: "", last_publish_at: null, last_error: "", ...(source.facebook || {}) },
      instagram: { connected: false, ig_user_id: "", username: "", uses_facebook_token: false, last_publish_at: null, last_error: "", ...(source.instagram || {}) },
      x: { connected: false, username: "", api_key_preview: "", access_token_preview: "", last_publish_at: null, last_error: "", ...(source.x || {}) },
    };
  };

  const load = async () => {
    try {
      setLoadError("");
      const [historyRes, accountsRes, templatesRes, settingsRes] = await Promise.all([
        api.get("/social/history"),
        api.get("/social/accounts"),
        api.get("/social/templates"),
        api.get("/social/settings"),
      ]);
      setHistory(Array.isArray(historyRes.data) ? historyRes.data : []);
      setAccounts(normalizeAccounts(accountsRes.data));
      setTemplates({ ...DEFAULT_TEMPLATES, ...((templatesRes.data && typeof templatesRes.data === "object") ? templatesRes.data : {}) });
      setSocialSettings((settingsRes.data && typeof settingsRes.data === "object") ? settingsRes.data : { social_public_base_url: "" });
    } catch (e) {
      setLoadError(e?.response?.data?.detail || "Errore caricamento configurazione social");
      setHistory([]);
      setAccounts(normalizeAccounts({}));
      toast.error(e?.response?.data?.detail || "Errore caricamento configurazione social");
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const setPlatformForm = (platform, patch) => {
    setForms(current => ({ ...current, [platform]: { ...(EMPTY_FORMS[platform] || {}), ...(current?.[platform] || {}), ...patch } }));
  };

  const setTemplate = (platform, patch) => {
    setTemplates(current => ({ ...current, [platform]: { ...(DEFAULT_TEMPLATES[platform] || {}), ...(current?.[platform] || {}), ...patch } }));
  };

  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x=>x!==id) : [...s, id]);

  const saveAccount = async (platform) => {
    try {
      await api.patch(`/social/accounts/${platform}`, forms[platform] || EMPTY_FORMS[platform] || {});
      toast.success("Account collegato");
      setForms(current => ({ ...current, [platform]: EMPTY_FORMS[platform] }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore collegamento");
    }
  };

  const disconnectAccount = async (platform) => {
    if (!window.confirm("Disconnettere questo account?")) return;
    try {
      await api.delete(`/social/accounts/${platform}`);
      toast.success("Account disconnesso");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore disconnessione");
    }
  };

  const testAccount = async (platform) => {
    try {
      await api.post(`/social/accounts/${platform}/test`);
      toast.success("Connessione valida");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connessione non valida");
    }
  };

  const publishTest = async (platform) => {
    try {
      await api.post(`/social/publish-test/${platform}`);
      toast.success(`Test pubblicato su ${platform}`);
      load();
    } catch (e) {
      toast.error(`${platform}: ${e?.response?.data?.detail || "Errore pubblicazione test"}`);
      load();
    }
  };

  const saveTemplates = async () => {
    try {
      const { data } = await api.patch("/social/templates", { templates });
      setTemplates(data);
      toast.success("Template salvati");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio template");
    }
  };

  const saveSocialSettings = async () => {
    try {
      const { data } = await api.patch("/social/settings", socialSettings);
      setSocialSettings(data);
      toast.success("Impostazioni social salvate");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio impostazioni social");
    }
  };

  const generatePreview = async () => {
    try {
      const { data } = await api.post("/social/preview", {
        templates,
        title: "Inter, svolta per il mercato",
        post: "I nerazzurri accelerano per rinforzare la rosa: la trattativa entra in una fase decisiva e sono attesi nuovi contatti nelle prossime ore.",
        hashtags: "#Inter #CalcioNews24 #Mercato",
        source: "CalcioNews24",
        feed_name: "CalcioNews24",
        link: "https://calciohub.ai",
      });
      setPreview(data || {});
      toast.success("Anteprima aggiornata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore anteprima");
    }
  };

  const publish = async () => {
    if (!text || selected.length === 0) {
      toast.error("Testo e piattaforme obbligatori");
      return;
    }
    for (const platform of selected) {
      try {
        const { data } = await api.post("/social/publish", { platform, text, image_url: imageUrl });
        toast.success(data.message);
      } catch (e) {
        toast.error(`${platform}: ${e?.response?.data?.detail || "Errore pubblicazione"}`);
      }
    }
    setText("");
    setImageUrl("");
    load();
  };

  const tpl = templates[activeTemplate] || DEFAULT_TEMPLATES[activeTemplate] || {};
  const normalizedAccounts = normalizeAccounts(accounts);
  const hasConnectedAccounts = socialPlatforms.some(platform => normalizedAccounts[platform.id]?.connected);

  return (
    <div data-testid="social-page">
      <PageHeader title="Social Automation 2.0" subtitle="Template, anteprime, account ufficiali e pubblicazione RSS multi-piattaforma.">
        <span className="text-xs font-mono text-emerald-300 px-3 py-1.5 rounded-md bg-emerald-600/10 border border-emerald-600/30">API UFFICIALI</span>
      </PageHeader>
      <PageContent>
        <Tabs defaultValue="accounts">
          <TabsList className="bg-slate-900 border border-slate-800">
            <TabsTrigger value="accounts">Account</TabsTrigger>
            <TabsTrigger value="templates">Template</TabsTrigger>
            <TabsTrigger value="preview">Anteprima</TabsTrigger>
            <TabsTrigger value="manual">Manuale</TabsTrigger>
            <TabsTrigger value="logs">Log</TabsTrigger>
          </TabsList>

          <TabsContent value="accounts" className="mt-4">
            {loadError && (
              <div className="mb-4 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200">
                {loadError}
              </div>
            )}
            {!hasConnectedAccounts && (
              <div className="mb-4 rounded-md border border-slate-800 bg-slate-900 p-4 text-sm text-slate-400">
                Nessun account collegato. Inserisci le credenziali ufficiali di una piattaforma per attivare la pubblicazione social.
              </div>
            )}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
              {socialPlatforms.map(platform => (
                <AccountCard
                  key={platform.id}
                  platform={platform}
                  account={normalizedAccounts[platform.id]}
                  form={forms?.[platform.id] || EMPTY_FORMS[platform.id]}
                  setForm={setPlatformForm}
                  onSave={saveAccount}
                  onDisconnect={disconnectAccount}
                  onTest={testAccount}
                  onPublishTest={publishTest}
                />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="templates" className="mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
              <div className="bg-slate-900 border border-slate-800 rounded-md p-3 space-y-2">
                {PLATFORMS.map(platform => {
                  const Icon = iconFor(platform.id);
                  return (
                    <button key={platform.id} onClick={()=>setActiveTemplate(platform.id)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm border ${activeTemplate === platform.id ? "bg-blue-600/15 border-blue-500/50 text-blue-200" : "bg-slate-950 border-slate-800 text-slate-400"}`}>
                      <Icon size={15} className={platform.color} />
                      {platform.label}
                    </button>
                  );
                })}
              </div>
              <div className="lg:col-span-3 bg-slate-900 border border-slate-800 rounded-md p-5 space-y-4">
                <div className="bg-slate-950 border border-slate-800 rounded-md p-3">
                  <Label>URL pubblico backend per immagini social</Label>
                  <div className="flex gap-2 mt-1">
                    <Input value={socialSettings.social_public_base_url || ""} onChange={e=>setSocialSettings({ social_public_base_url: e.target.value })} className="bg-slate-900 border-slate-800 font-mono text-xs" placeholder="https://tuodominio.it oppure http://87.106.191.138:8001" />
                    <Button onClick={saveSocialSettings} variant="outline" className="border-slate-700">Salva</Button>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">Necessario per Instagram e per qualsiasi piattaforma che richiede immagini raggiungibili pubblicamente.</div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-bold text-white">Template {PLATFORMS.find(p=>p.id===activeTemplate)?.label}</div>
                    <div className="text-xs text-slate-500 mt-1">Placeholder: {"{title} {post} {hashtags} {cta} {source} {link}"}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label>Attivo</Label>
                    <Switch checked={tpl.enabled !== false} onCheckedChange={v=>setTemplate(activeTemplate, { enabled: v })} />
                  </div>
                </div>
                <div><Label>Formato testo</Label><Textarea value={tpl.text || ""} onChange={e=>setTemplate(activeTemplate, { text: e.target.value })} rows={8} className="bg-slate-950 border-slate-800 mt-1 font-mono text-xs" /></div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div><Label>Hashtag</Label><Input value={tpl.hashtags || ""} onChange={e=>setTemplate(activeTemplate, { hashtags: e.target.value })} className="bg-slate-950 border-slate-800 mt-1" /></div>
                  <div><Label>CTA</Label><Input value={tpl.cta || ""} onChange={e=>setTemplate(activeTemplate, { cta: e.target.value })} className="bg-slate-950 border-slate-800 mt-1" /></div>
                </div>
                <Button onClick={saveTemplates} className="bg-blue-600 hover:bg-blue-700"><Save size={16} className="mr-2" />Salva template</Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="preview" className="mt-4">
            <div className="flex justify-end mb-4">
              <Button onClick={generatePreview} className="bg-blue-600 hover:bg-blue-700"><Eye size={16} className="mr-2" />Genera anteprima</Button>
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {PLATFORMS.map(platform => <PreviewCard key={platform.id} platform={platform.id} item={preview[platform.id]} />)}
            </div>
          </TabsContent>

          <TabsContent value="manual" className="mt-4">
            <div className="bg-slate-900 border border-slate-800 rounded-md p-5">
              <h3 className="font-bold text-white mb-3">Pubblicazione manuale</h3>
              <Textarea data-testid="social-text" value={text} onChange={e=>setText(e.target.value)} rows={7} placeholder="Testo da pubblicare..." className="bg-slate-950 border-slate-800 resize-none" />
              <Input value={imageUrl} onChange={e=>setImageUrl(e.target.value)} className="bg-slate-950 border-slate-800 mt-3 font-mono text-xs" placeholder="URL immagine pubblica opzionale" />
              <div className="grid grid-cols-3 gap-2 mt-4">
                {socialPlatforms.map(p => {
                  const Icon = iconFor(p.id);
                  const active = selected.includes(p.id);
                  return (
                    <button key={p.id} onClick={()=>toggle(p.id)} data-testid={`platform-${p.id}`}
                      className={`p-4 rounded-md border flex items-center gap-3 transition-colors ${active ? "bg-blue-600/15 border-blue-500/50" : "bg-slate-950 border-slate-800 hover:border-slate-700"}`}>
                      <Icon className={p.color} size={22} /><span className="text-sm font-semibold text-white">{p.label}</span>
                    </button>
                  );
                })}
              </div>
              <Button onClick={publish} data-testid="social-publish" className="mt-4 w-full bg-blue-600 hover:bg-blue-700 h-11"><Send size={16} className="mr-2" />Pubblica su {selected.length} piattaforma/e</Button>
            </div>
          </TabsContent>

          <TabsContent value="logs" className="mt-4">
            <div className="bg-slate-900 border border-slate-800 rounded-md p-5">
              <h3 className="font-bold text-white mb-3">Log pubblicazioni</h3>
              <div className="space-y-2 max-h-[620px] overflow-y-auto scrollbar-thin">
                {history.length === 0 && <div className="text-sm text-slate-500">Nessuna pubblicazione.</div>}
                {history.map(h => (
                  <div key={h.id} className="p-3 rounded-md bg-slate-950 border border-slate-800 text-xs">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-bold text-blue-400 uppercase">{h.platform}</span>
                      <span className="text-slate-500 font-mono">{new Date(h.created_at).toLocaleString("it-IT")}</span>
                    </div>
                    <div className="flex items-center gap-1 mb-1">
                      {h.ok ? <CheckCircle2 size={13} className="text-emerald-400" /> : <AlertTriangle size={13} className="text-red-400" />}
                      <span className={h.ok ? "text-emerald-300" : "text-red-300"}>{h.ok ? "Pubblicato" : h.error || "Errore"}</span>
                    </div>
                    <div className="text-slate-300 line-clamp-3">{h.text}</div>
                    {h.image_url && <div className="text-[10px] text-slate-500 font-mono mt-1 truncate">{h.image_url}</div>}
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </PageContent>
    </div>
  );
}
